import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../../context/LoadingContext";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../../Requirement/components/common/SearchFilter";
import { fetchData, postQuery } from "../../../Requirement/services/RequirementServices";
import OfferDetails from "./component/OfferDetails";
import { Typography, TextField } from "@mui/material";
import dayjs from "dayjs";
import Requirements from "./RequirementTab";

const ReqShortlistedOffers = () => {
  const { setLoading, loading } = useLoading();
  const [offerList, setOfferList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [error, setError] = useState(null);
  const [INRToUsd] = useState(0.012);
 
    const [activeUser, setActiveUser] = useState("");
  const [buyerInfo, setBuyerInfo] = useState([]);
const navigate = useNavigate();
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    deliveryDate: "",
    requirementDate: "",
    status: "",
  });

  const offerStatusOptions = [
    { value: "", label: "All" },
    { value: "BAFO", label: "BAFO" },
    { value: "Firmoffer", label: "Firm Offer" },
    { value: "Indicative", label: "Indicative" },
  ];
    const getBuyerInfo = useCallback(async (buyerEmail) => {
      try {
        const response = await fetchData({
          target: "listAMTPLUsers",
          userOrder: "AMTPLUserID",
          rowStart: 0,
          pageSize: 100,
          filterTerms: { EmailID: buyerEmail },
        });
        setBuyerInfo(response.items || []);
      } catch (error) {
        console.error("Error loading buyer info:", error);
        setBuyerInfo([]);
      }
    }, []);
  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_GLOBAL_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/global-partner");
        } else {
          setActiveUser(user.attributes.email);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/global-partner");
      }
    };
    checkUser();
    
  }, [navigate, getBuyerInfo]);
  const ShortlistedOffers = useCallback(
    async (newRowStart = rowStart) => {
      setLoading(true);
      setError(null);
      try {
        // Build filterTerms dynamically based on searchValues
        const filterTerms = {
          CategoryType: "requirement",
          OfferStatusExtended: "Shortlist",
          GPEmailID: activeUser,
          Deleted: 0,
        };

        // Add search filters only if they have values
        if (searchValues.requirementNumber) {
          filterTerms.RequirementNumber = searchValues.requirementNumber;
        }
        if (searchValues.deliveryDate) {
          // Format date to match API expectations (e.g., YYYY-MM-DD)
          filterTerms.DeliveryPeriodFrom = dayjs(searchValues.deliveryDate).format("YYYY-MM-DD");
        }
        if (searchValues.requirementDate) {
          // Format date to match API expectations (e.g., YYYY-MM-DD)
          filterTerms.CreatedAt = dayjs(searchValues.requirementDate).format("YYYY-MM-DD");
        }
        if (searchValues.status) {
          filterTerms.OfferStatus = searchValues.status;
        }

        const body = {
          target: "listBuyerExtendedRequirementsItems",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms,
        };

        const response = await fetchData(body);
        if (Array.isArray(response.items) || Array.isArray(response)) {
          setOfferList(response.items || response || []);
          setTotalItems(response.total || 0);
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error loading matching offers:", error);
        setError("Failed to load matching offers. Please try again.");
        setOfferList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading, activeUser, searchValues]
  );

  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "deliveryDate",
      label: "Delivery Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, deliveryDate: value })),
      value: searchValues.deliveryDate,
    },
    {
      name: "requirementDate",
      label: "Requirement Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementDate: value })),
      value: searchValues.requirementDate,
    },
    {
      name: "status",
      label: "Offer Status",
      type: "dropdown",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, status: value })),
      value: searchValues.status,
      options: offerStatusOptions,
    },
  ];

  const handlePageChange = useCallback(
    (page) => {
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
    },
    [pageSize]
  );

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const formatPrice = (value) => {
    if (!value || value === "") return "0.00";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  const convertToUSD = (inrValue) => {
    if (!inrValue || inrValue === "") return "0.00";
    const usdValue = parseFloat(inrValue) * INRToUsd;
    return isNaN(usdValue) ? "0.00" : usdValue.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Requirement Number" },
    // { id: "BuyerName", numeric: false, label: "Buyer Name" },
    { id: "BuyerEmailID", numeric: false, label: "Buyer Email ID" },
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    {
      id: "Quantity",
      numeric: true,
      label: "Quantity (MT)",
      renderCell: (row) => formatPrice(row.Quantity),
    },
    {
      id: "CIFSalePrice",
      numeric: true,
      label: "CIF Sale Price",
      renderCell: (row) => convertToUSD(row.CIFSalePrice),
    },
    { id: "Country", numeric: false, label: "Origin Country" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "Incoterm", numeric: false, label: "INCO Terms" },
    {
      id: "OfferStatus",
      numeric: false,
      label: "Offer Type",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.OfferStatus) {
          case "BAFO":
            bgColor = "#e0f2fe";
            textColor = "#0369a1";
            break;
          case "Firmoffer":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
            row.OfferStatus = "Indicative"; // Set default status
            break;
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {row.OfferStatus}
          </span>
        );
      },
    },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires",
      renderCell: (row) => formatDate(row.Expires),
    },
    {
      id: "KYBStatus",
      numeric: false,
      label: "Seller Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.KYBStatus) {
          case "Approved":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {row.KYBStatus === "Approved" ? "Verified" : "Unverified"}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    ShortlistedOffers();
  }, [ShortlistedOffers]);

  const handleRowClick = useCallback((row) => {
    setSelectedOffer(row);
    console.log("row", row);
  }, []);

  const handleSearch = () => {
    setRowStart(0); // Reset to first page
    ShortlistedOffers(0); // Fetch data with new filters
  };

  const handleRemoveOffer = async () => {
    if (!selectedOffer?.BuyerItemID) {
      console.error("No BuyerItemID found for selected offer");
      return;
    }

    if (!window.confirm("Are you sure you want to remove this offer from your shortlist?")) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const body = {
        target: "buyerExtendedOfferUpdate",
        BuyerItemExtendedInput: {
          Deleted: 1,
          BuyerItemExtendedID: selectedOffer.BuyerItemExtendedID,
        },
      };

      const result = await postQuery(body, "backendops");

      if (result) {
        setSelectedOffer(null);
        await ShortlistedOffers(rowStart);
        console.log("Offer removed successfully");
      }
    } catch (error) {
      console.error("Error removing offer:", error);
      setError("Failed to remove offer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const Button = ({ label, onClick }) => {
    return (
      <button className="btn-action-re" onClick={onClick}>
        {label}
      </button>
    );
  };

  const selectedRows = selectedOffer ? [selectedOffer.GPOfferID || selectedOffer.id] : [];

  return (
    <div className="matching-offers-container">
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Shortlisted Offers</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>
      {loading && <div className="loading">Loading offers...</div>}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && offerList.length === 0 && (
        <div className="empty-state">No matching offers found.</div>
      )}
      {!loading && offerList.length > 0 && (
        <DataTable
          columns={columns}
          data={offerList}
          total={totalItems}
          title="Matching Offers Information"
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          onRowClick={handleRowClick}
          selectedRows={selectedRows}
        />
      )}
      {selectedOffer && (
        <>
          <div className="offer-detail1">
            <div className="header-text-re">
              <div className="head-text">Offer Details</div>
              {/* <div className="button-group">
                <Button label="Remove" onClick={handleRemoveOffer} />
              </div> */}
            </div>
            <OfferDetails
              offer={selectedOffer}
              btnShortlist={false}
              btnSendQuery={false}
              btnRequestQuote={false}
              btnRequestSpecifications={false}
              btnRequestBestPrice={false}
              btnNegotiate={false}
              btnRequestShippingDetails={false}
              btnLogisticsSupport={false}
              btnRaiseQuery={true}
            />
          </div>

          {selectedOffer && selectedOffer.RequirementsID && (
            <div className="requirement-detail" style={{ marginTop: "20px" }}>
              <div className="header-text-re">
                <div className="head-text">Requirement - {selectedOffer?.RequirementNumber || ""}</div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Typography variant="body2">Product</Typography>
                  <TextField
                    value={selectedOffer?.ProductName || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                    size="small"
                  />
                </div>
                <div className="col-md-3">
                  <Typography variant="body2">Grade</Typography>
                  <TextField
                    value={selectedOffer?.Grade || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                    size="small"
                  />
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Origin</Typography>
                    <TextField
                      value={selectedOffer.OriginCountry || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Destination</Typography>
                    <TextField
                      value={selectedOffer.DeliveryLocation ? selectedOffer.DeliveryLocation.split("~")[0] : ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Quantity (MT)</Typography>
                    <TextField
                      value={selectedOffer.RqQuantity || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Inco Terms</Typography>
                    <TextField
                      value={selectedOffer.Incoterm || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Payment Terms</Typography>
                    <TextField
                      value={selectedOffer.PaymentTerms || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Packaging</Typography>
                    <TextField
                      value={(() => {
                        const packaging = selectedOffer.ProductPackaging || "";
                        if (packaging.includes("item=")) {
                          const match = packaging.match(/item=([^,}]+)/);
                          return match ? match[1].trim() : packaging;
                        }
                        return packaging;
                      })()}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery Port</Typography>
                    <TextField
                      value={selectedOffer.DeliveryLocation ? selectedOffer.DeliveryLocation.split("~")[1] : ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery (Start)</Typography>
                    <TextField
                      value={selectedOffer.DeliveryPeriodFrom ? dayjs(selectedOffer.DeliveryPeriodFrom).format("DD-MM-YYYY") : ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery (End)</Typography>
                    <TextField
                      value={selectedOffer.DeliveryPeriodTill ? dayjs(selectedOffer.DeliveryPeriodTill).format("DD-MM-YYYY") : ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Valid Until</Typography>
                    <TextField
                      value={selectedOffer.ValidUntil ? dayjs(selectedOffer.ValidUntil).format("DD-MM-YYYY") : ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReqShortlistedOffers;